import { HubConnection } from '@microsoft/signalr';
import { GUID } from '../guids';
import { ClientCode, ClientConfig } from './clients';

export interface Model {
  readonly alerts: ReadonlyArray<AlertMessage>
  readonly alertViewSettings: AlertViewSettings
  readonly alertLevel: AlertLevel
  readonly automaticRedButtonEnabled: AutomaticRedButtonEnabled
  readonly mutedClientMetrics: MutedClientMetricsDict
  readonly clients: ReadonlyArray<ClientExposuresTransactions>
  readonly clientSettings: ReadonlyArray<ClientConfig>
  readonly clientSearch: string | undefined
  readonly dataLastUpdated: Date | undefined
  readonly greenButtonState: ButtonState
  readonly initialised: boolean
  readonly languageName: string
  readonly localTime: Date
  readonly metricLevels: AlertLevelsDict
  readonly clientDetailsPanelOpen: boolean
  readonly selectedClientCode: ClientCode | undefined
  readonly isAddingClient: boolean
  readonly currentPage: Pages
  readonly totalMessagesCount: number
  readonly dailyTotalNewOrdersValue: number
  readonly dailyExecutedOrdersValue: number
  readonly lastRJEMessageTimestamp: Date | undefined
  readonly user: User
  readonly hubs: Hubs
  readonly redButtonState: ButtonState
  readonly redButtonConfirmationCode: string
  readonly greenButtonConfirmationCode: string
  readonly orderNoticeAdminResult: ClientOrderNoticeAdminResult | undefined
  readonly toasts: ToastsList
}

/**
 * Should store toasts in oldest-first order.
 */
export type ToastsList = ReadonlyArray<Toast>

export type AutomaticRedButtonEnabled = 'Enabled' | 'Disabled' | 'Unknown'

export interface Toast {
  readonly id: GUID
  readonly content: string
  readonly header: string
}

export interface ClientOrderNoticeAdminResult {
  readonly resultType: OrderNoticeAdminButton
  readonly resultMessage: string
  readonly success: boolean
  readonly virtualServerId: string
}

export interface HubNotConnected {
  readonly state: 'NOT_CONNECTED'
}

export interface HubConnected {
  readonly state: 'CONNECTED'
  readonly connection: HubConnection
  readonly rjeConnectionStatus: RJEConnectionStatus
}

export interface HubConnecting {
  readonly state: 'CONNECTING'
}

export interface HubRetryAfter {
  readonly state: 'RETRY_AFTER'
  readonly retryAfterDate: Date
}

export type HubState =
  | HubConnected
  | HubConnecting
  | HubNotConnected
  | HubRetryAfter

export interface Hubs {
  readonly clientData: HubState
}

export type AlertLevel = 'Baseline' | Severity

export interface HasCode {
  readonly code: ClientCode
}
export type Severity = 'Breach' | 'Warn' | 'Notify'

export interface VirtualServerInfo {
  readonly serverId: string
  readonly connectionStatus: VirtualServerConnectionStatus
}

type VirtualServerConnectionStatus =
  | RedButtoned
  | Green
  | Redbuttoning
  | Greenbuttoning
  | RedError
  | GreenError

export interface RedButtoned {
  readonly type: 'RedButtoned'
}

export interface Green {
  readonly type: 'Green'
}

export interface Redbuttoning {
  readonly type: 'Redbuttoning'
}

export interface Greenbuttoning {
  readonly type: 'Greenbuttoning'
}

export interface RedError {
  readonly type: 'RedError'
  readonly Error: string
}

export interface GreenError {
  readonly type: 'GreenError'
  readonly Error: string
}

export interface ClientExposuresTransactions extends HasCode {
  readonly rc2exposurePercent: number
  readonly rc2exposureInYen: number
  readonly rc2exposureLimitInYen: number
  readonly rc6unactionedOrders: number
  readonly rc6unactionedOrdersLimit: number
  readonly rc6unactionedOrdersPercent: number
  readonly rc7messagesPerSecond: number
  readonly rc7messagesPerSecondPercent: number
  readonly revExposureOrdersValue: number
  readonly revExposureOrdersLimit: number
  readonly revExposureOrdersPercent: number
  readonly maxRevExposureOrdersValue: number
  readonly maxRevExposureOrdersPercent: number
  readonly historicMaxRevExposureValue: number
  readonly historicMaxRevExposurePercent: number
}

export interface DashboardModel {
  readonly lastUpdated: Date
  readonly totalMessagesCount: number
  readonly dailyTotalNewOrdersValue: number
  readonly dailyExecutedOrdersValue: number
  readonly lastRJEMessageTimestamp: Date
  readonly clients: ReadonlyArray<ClientExposuresTransactions>
}

export interface RawDashboardResponse {
  readonly data: {
    readonly [key: string]: ClientRiskData // key is a Symbol, like `FENX`
  }
  readonly latestMessageTimestamp: Date
  readonly totalMessageCount: number
  readonly dailyTotalNewOrderValue: number
  readonly dailyExecutedOrderValue: number
}

export interface ClientRiskData {
  readonly rc2: ClientRiskMetric
  readonly rc6: ClientRiskMetric
  readonly rev: ClientRiskMetric
  readonly maxRev: ClientRiskMetric
  readonly historicMaxRev: ClientRiskMetric
}

export interface ClientRiskMetric {
  readonly value: number
  readonly percentValue: number
  readonly breachLimit: number
}

export interface AlertMessage {
  readonly alertSeverity: Severity
  readonly alertTimestamp: Date
  readonly clientId: GUID
  readonly companyCode: ClientCode | Unknown
  readonly companyName: string | Unknown
  readonly exceeded: boolean
  readonly limitType: LimitType
  readonly percentValue: number | undefined
  readonly rjeMessageNumber: number
  readonly rjeMessageTimestamp: Date
  readonly sequenceNumber: number
  readonly thresholdPercent: number | undefined
  readonly value: number | undefined
  readonly virtualServerNumber: string
}

export type AlertColumnName =
  | 'when'
  | 'direction'
  | 'levels'
  | 'client'
  | 'metric'
  | 'sequenceNumber'
  | 'rjeMessageNumber'
  | 'thresholdPercent'
  | 'triggerPercent'

export type SortType = 'none' | 'ascending' | 'descending'

export type AlertTableSortSetting = [
  keyof AlertMessage,
  'ascending' | 'descending'
]

export type AlertTableSortSettings = ReadonlyArray<AlertTableSortSetting>

// TODO: add pagination support (to this interface?)
export interface AlertViewSettings {
  readonly sortBy: AlertTableSortSettings
  readonly openColumnMenu: string | undefined
  readonly filterClient: undefined | ClientCode
  readonly filterDirection: 'all' | 'exceeding' | 'retreating from'
  readonly filterSeverityOrLevel: ['all'] | ['severity', Severity] | ['level', AlertLevel]
  readonly filterMetric: 'all' | LimitType
  readonly showOnlyActiveAlerts: boolean
}

interface UserInfo {
  readonly username: string
  readonly hasBusinessAdminRole: boolean
  readonly hasITAdminRole: boolean
  readonly hasTVModeRole: boolean
  readonly hasRiskUserRole: boolean
}

export type User =
  | NotLoggedIn
  | FailedToLogIn
  | LoggingIn
  | LoggedIn
  | FailedToLogout

export interface NotLoggedIn {
  readonly type: 'NotLoggedIn'
}

export interface FailedToLogIn {
  readonly type: 'FailedToLogIn'
}

export interface FailedToLogout extends UserInfo {
  readonly type: 'FailedToLogout'
}

export interface LoggingIn {
  readonly type: 'LoggingIn'
}

export interface LoggedIn extends UserInfo {
  readonly type: 'LoggedIn'
}

export type ButtonState = 'Depressed' | 'Raised' | 'Disabled'

export type Pages =
  | 'Alerts'
  | 'Main Chart'
  | 'Manage Clients'
  | 'Red Buttons'
  | 'Suspensions'
  | 'TV Mode'
  | 'Settings'
  | 'Reports'

const PageUrls: Map<Pages, string> = new Map([
  ["Alerts", "/alerts"],
  ["Main Chart", "/"],
  ["Manage Clients", "/manage-clients"],
  ["Red Buttons", "/red-buttons"],
  ["Suspensions", "/suspensions"],
  ["TV Mode", "/tv-mode"],
  ["Settings", "/settings"],
  ["Reports", "/reports"],
]);

export const getPageUrl = (page: Pages): string => {
  const url = PageUrls.get(page);

  if(url === undefined) {
    console.warn(`Url missing for ${page}, applying default of /`);
    return "/"
  }
  else {
    return url;
  }
}

export type LimitType =
  | 'RC1_total_clip_size'
  | 'RC2_exposure'
  | 'RC6_unactioned_orders'
  | 'RC7_messages_per_second'
  | 'Virtual_server_suspension'
  | 'Realistic_exposure_value'
  | 'Historic_Max_Realistic_exposure'

export type Permission = 'canViewClientDetails' | 'canViewAlerts' | 'canViewMainChart' | 'canRedButton' | 'canViewTvModeAndSettings'

export interface RolePermissions {
  readonly businessAdmin: boolean
  readonly itAdmin: boolean
  readonly tvMode: boolean
  readonly riskUser: boolean
}

export type HubChannel =
  | 'BroadcastMetricSamples'
  | 'BroadcastAlert'
  | 'BroadcastAlertsHistory'
  | 'BroadcastRjeConnectionStatus'

/**
 * This doesn't allow representing the state where we haven't yet seen levels
 * for all the metrics; should it?
 */
export interface AlertLevels {
  readonly rc1: AlertLevel
  readonly rc2: AlertLevel
  readonly rc6: AlertLevel
  readonly rc7: AlertLevel
  readonly virtualServerSuspension: AlertLevel
  readonly rev: AlertLevel
  readonly maxRev: AlertLevel
  readonly historicMaxRev: AlertLevel
}

/**
 * ⓘ Use the client's shortcode for the key.
 *
 * (Can't use the GUID, because the metric samples aren't guaranteed to have
 * guids...)
 */
export type AlertLevelsDict = Record<string, AlertLevels | undefined>

export type MutedMetrics = Record<LimitType, 'muted' | 'not_muted'>

/**
 * ⓘ Use the client's guid for the key.
 *
 * Metrics default to 'not_muted'.
 */
export type MutedClientMetricsDict = Record<string, MutedMetrics | undefined>

export type RJEConnectionStatus =
  | 'Unknown'
  | 'Connected'
  | 'Connecting'
  | 'Retrying'
  | 'ParseError'

export interface OrderNoticeAdminResponse {
  readonly virtualServerId: string
  readonly message: string
  readonly success: boolean
  readonly button: OrderNoticeAdminButton
}

export type OrderNoticeAdminButton = 'GREEN' | 'RED'

export type Unknown = 'Unknown';
